import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Search, Loader2, Package, MapPin, User, Clock,
  Link2, AlertCircle, Shirt, Hash, FileText, ArrowDownToLine,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STAGE_COLORS = {
  'Greige Section': 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'Greige Storage': 'bg-sky-100 text-sky-700 border-sky-300',
};

const OP_ICON = {
  'Bundle Created': Package,
  'Sort No Assignment': Hash,
  'Greige Inspection': Shirt,
  'Pinning': Package,
  'Marker Writing': FileText,
  'Packing': Package,
  'Bundle Split': Package,
  'Bundle Receipt': ArrowDownToLine,
  'Put Away': MapPin,
  'Location Transfer': MapPin,
  'Picking': Package,
  'Internal Transfer': MapPin,
  'Staging': Package,
  'Picked From Bin': Package,
};

const fmtTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

const BundleUSNTrace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromStorage = location.pathname.startsWith('/sections/greige-storage');
  const backRoute = fromStorage ? '/sections/greige-storage' : '/sections/greige';
  const sectionLabel = fromStorage ? 'Greige Storage' : 'Greige Section';

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      toast.warning('Enter at least 3 characters (bundle barcode or USN)');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      const res = await apiClient.get('/greige/trace', { params: { q } });
      setResult(res.data);
      if (res.data?.match_type === 'not_found') {
        toast.error(res.data.message || 'Not found');
      } else {
        toast.success(`Found ${res.data?.total_events || 0} event(s)`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Search failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const bundle = result?.bundle;
  const events = result?.events || [];
  const usns = result?.usns || [];
  const focusUsn = result?.focus_usn;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="trace-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Bundle / USN Trace</h1>
              <p className="text-sm text-[#E8DCC8]">{sectionLabel} → universal lifecycle search</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Search */}
        <Card className="border-[#E5DFD0]">
          <CardContent className="py-5">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row items-stretch gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter bundle barcode (BUN...) or USN code"
                  className="pl-10 h-11 text-base"
                  data-testid="trace-search-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="h-11 px-6 bg-[#2D5F4F] hover:bg-[#244a3e] text-white"
                data-testid="trace-search-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Search</>
                )}
              </Button>
            </form>
            <p className="text-xs text-[#5A5F6E] mt-2">
              Auto-detect: starts with <span className="font-mono font-semibold">BUN</span> → bundle, otherwise USN.
              Trace covers Greige Section + Greige Storage events with linked pick-slip / shipment-request IDs.
            </p>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-red-300 bg-red-50">
            <CardContent className="py-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {result?.match_type === 'not_found' && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="py-6 text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
              <p className="text-amber-700 font-medium">{result.message}</p>
            </CardContent>
          </Card>
        )}

        {bundle && (
          <>
            {/* Bundle Meta */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="w-5 h-5 text-[#2D5F4F]" />
                  Bundle Details
                  <Badge variant="outline" className="font-mono ml-2" data-testid="trace-bundle-barcode">
                    {bundle.bundle_barcode}
                  </Badge>
                  {focusUsn && (
                    <Badge className="bg-blue-100 text-blue-700 border border-blue-300 font-mono ml-2">
                      Focus USN: {focusUsn}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <MetaRow label="Sort No" value={bundle.sort_no} />
                  <MetaRow label="Design / FG" value={bundle.design_name || bundle.fg_name} />
                  <MetaRow label="Color" value={bundle.color_name} />
                  <MetaRow label="Vendor" value={bundle.vendor_name} />
                  <MetaRow label="USN Count" value={bundle.usn_count} />
                  <MetaRow label="Greige Status" value={bundle.greige_status} />
                  <MetaRow label="Storage Status" value={bundle.storage_status} />
                  <MetaRow label="Current Location" value={bundle.location_id || bundle.footprint} />
                  <MetaRow label="Weight (kg)" value={bundle.weight_kg} />
                  <MetaRow label="Latest Stage" value={bundle.latest_stage} highlight />
                </div>
              </CardContent>
            </Card>

            {/* Pick-slip / shipment linkages */}
            {(result.pick_slip_links || []).length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Link2 className="w-5 h-5 text-purple-600" /> Downstream Linkages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Pick Slip</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Picker</TableHead>
                          <TableHead>GS Request</TableHead>
                          <TableHead>Category Request</TableHead>
                          <TableHead>Picked</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.pick_slip_links.map((l) => (
                          <TableRow key={l.pick_slip_id}>
                            <TableCell className="font-mono text-xs">{l.pick_slip_id}</TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-700 border-0">
                                {l.pick_slip_status || '-'}
                              </Badge>
                            </TableCell>
                            <TableCell>{l.picker || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">{l.request_id || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {l.category_request_id || '-'}
                            </TableCell>
                            <TableCell>
                              {l.picked ? (
                                <span className="text-green-600 text-xs">
                                  ✓ {fmtTime(l.picked_at)} by {l.picked_by || '-'}
                                </span>
                              ) : <span className="text-gray-400">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Event Timeline */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-amber-600" /> Event Timeline
                  <Badge variant="outline" className="ml-2" data-testid="trace-event-count">
                    {events.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">No events recorded yet.</p>
                ) : (
                  <div className="space-y-3">
                    {events.map((e, idx) => {
                      const Icon = OP_ICON[e.operation] || Package;
                      const stageClass = STAGE_COLORS[e.stage] || 'bg-gray-100 text-gray-700';
                      return (
                        <div
                          key={idx}
                          className="flex gap-4 items-start p-3 rounded-lg border border-[#E5DFD0] bg-white hover:shadow-sm transition"
                          data-testid={`trace-event-${idx}`}
                        >
                          <div className={`rounded-full p-2 ${stageClass} flex-shrink-0`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className={`${stageClass} border`}>
                                {e.stage}
                              </Badge>
                              <span className="font-semibold text-[#1A1F2E]">{e.operation}</span>
                              {e.qty != null && (
                                <Badge variant="outline" className="text-xs">
                                  {e.qty} USN
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[#5A5F6E]">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {fmtTime(e.timestamp)}
                              </span>
                              {e.user_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" /> {e.user_name}
                                </span>
                              )}
                            </div>
                            {(e.details && Object.values(e.details).some((v) => v)) && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {Object.entries(e.details).map(([k, v]) =>
                                  v ? (
                                    <span
                                      key={k}
                                      className="bg-gray-50 text-gray-700 rounded px-2 py-0.5 border border-gray-200"
                                    >
                                      <span className="text-gray-500">{k}:</span> <span className="font-mono">{String(v)}</span>
                                    </span>
                                  ) : null
                                )}
                              </div>
                            )}
                            {e.linkage && (
                              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                {Object.entries(e.linkage).map(([k, v]) =>
                                  v ? (
                                    <span
                                      key={k}
                                      className="bg-purple-50 text-purple-700 rounded px-2 py-0.5 border border-purple-200 font-mono"
                                    >
                                      <Link2 className="w-3 h-3 inline mr-1" />
                                      {k}: {v}
                                    </span>
                                  ) : null
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* USN List (siblings when USN search) */}
            {usns.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Hash className="w-5 h-5 text-indigo-600" /> USNs in Bundle
                    <Badge variant="outline" className="ml-2">{usns.length}</Badge>
                    {focusUsn && (
                      <span className="text-sm font-normal text-[#5A5F6E]">
                        (siblings of <span className="font-mono">{focusUsn}</span>)
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>USN Code</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Damages</TableHead>
                          <TableHead>Inspected At</TableHead>
                          <TableHead>Inspected By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usns.map((u) => {
                          const isFocus = focusUsn && u.usn_code === focusUsn;
                          return (
                            <TableRow
                              key={u.usn_code}
                              className={isFocus ? 'bg-blue-50' : ''}
                              data-testid={`trace-usn-row-${u.usn_code}`}
                            >
                              <TableCell className={`font-mono text-xs ${isFocus ? 'font-bold text-blue-700' : ''}`}>
                                {u.usn_code}
                                {isFocus && <Badge className="ml-2 bg-blue-600 text-white text-xs">Searched</Badge>}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={`${
                                    u.is_damage
                                      ? 'bg-red-100 text-red-700 border-red-300'
                                      : 'bg-green-100 text-green-700 border-green-300'
                                  } border`}
                                >
                                  {u.is_damage ? 'DAMAGED' : (u.status || '-').toUpperCase()}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                {Array.isArray(u.damages) && u.damages.length > 0
                                  ? u.damages.map((d, i) =>
                                      typeof d === 'string' ? d : (d?.damage_type || JSON.stringify(d))
                                    ).join(', ')
                                  : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-gray-600">{fmtTime(u.inspected_at)}</TableCell>
                              <TableCell className="text-xs">{u.inspected_by || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {!result && !loading && (
          <Card className="border-[#E5DFD0] bg-[#FFFEFB]">
            <CardContent className="py-10 text-center">
              <Search className="w-10 h-10 mx-auto text-[#2D5F4F] mb-3" />
              <p className="text-[#1A1F2E] font-medium">Start with a search above</p>
              <p className="text-sm text-[#5A5F6E] mt-1">
                Enter a bundle barcode (<span className="font-mono">BUN...</span>) or any USN code to see its full Greige-side history.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

const MetaRow = ({ label, value, highlight }) => (
  <div>
    <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">{label}</p>
    <p className={`font-semibold ${highlight ? 'text-[#2D5F4F]' : 'text-[#1A1F2E]'}`}>
      {value || value === 0 ? String(value) : '-'}
    </p>
  </div>
);

export default BundleUSNTrace;
